<template>
  <v-container class="plz-surcharge-container">
    <h2 class="mb-3">Stauaufschläge je PLZ-Gebiet</h2>

    <v-alert type="info" variant="tonal" density="compact" class="mb-4">
      <div class="d-flex align-center flex-wrap ga-2">
        <span>
          Stauaufschläge für <strong>{{ officeName }}</strong>
        </span>
        <v-chip v-if="rules.length === 0" color="warning" size="small">
          <v-icon start>mdi-information</v-icon> Keine Aufschläge hinterlegt
        </v-chip>
        <v-chip v-else color="success" size="small">
          <v-icon start>mdi-check-circle</v-icon>
          {{ plzCount }} {{ plzCount === 1 ? "PLZ-Gebiet" : "PLZ-Gebiete" }},
          {{ rules.length }} {{ rules.length === 1 ? "Regel" : "Regeln" }}
        </v-chip>
      </div>
    </v-alert>

    <p class="text-body-2 text-medium-emphasis mb-4">
      Ein Aufschlag wirkt auf die Wegzeiten aller Wohnadressen der jeweiligen
      Postleitzahl: Zeit × (1 + Prozent/100) + Minuten. Beides ist kombinierbar.
      Bequemer geht es in der Kartenansicht, dort lassen sich mehrere Gebiete
      auf einmal auswählen.
    </p>

    <v-alert
      v-if="error"
      type="error"
      variant="tonal"
      density="compact"
      class="mb-4"
      closable
      @click:close="error = ''"
    >
      {{ error }}
    </v-alert>

    <!-- Neue Regel -->
    <v-card variant="outlined" class="mb-4">
      <v-card-text>
        <v-row dense align="center">
          <v-col cols="12" sm="3">
            <v-text-field
              v-model="newPlz"
              label="PLZ"
              density="compact"
              maxlength="5"
              :error-messages="newPlzError"
              hide-details="auto"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="3">
            <v-select
              v-model="newTransport"
              :items="transportItems"
              item-title="label"
              item-value="value"
              label="Verkehrsmittel"
              density="compact"
              hide-details
            ></v-select>
          </v-col>
          <v-col cols="6" sm="2">
            <v-text-field
              v-model.number="newPct"
              type="number"
              min="0"
              label="Prozent"
              suffix="%"
              density="compact"
              hide-details
            ></v-text-field>
          </v-col>
          <v-col cols="6" sm="2">
            <v-text-field
              v-model.number="newMinutes"
              type="number"
              min="0"
              label="Minuten"
              suffix="min"
              density="compact"
              hide-details
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="2">
            <v-btn
              color="primary"
              variant="flat"
              block
              :disabled="!canAdd"
              :loading="saving"
              @click="addRule"
            >
              Hinzufügen
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Bestehende Regeln -->
    <v-table v-if="rules.length > 0" density="compact">
      <thead>
        <tr>
          <th>PLZ</th>
          <th>Verkehrsmittel</th>
          <th style="width: 140px">Prozent</th>
          <th style="width: 140px">Minuten</th>
          <th style="width: 60px"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="rule in sortedRules" :key="ruleKey(rule)">
          <td>{{ rule.plz }}</td>
          <td>{{ labelFor(rule.transport_type) }}</td>
          <td>
            <v-text-field
              :model-value="rule.surcharge_pct"
              type="number"
              min="0"
              density="compact"
              variant="plain"
              hide-details
              suffix="%"
              @update:model-value="
                (value) => updateRule(rule, 'surcharge_pct', value)
              "
            ></v-text-field>
          </td>
          <td>
            <v-text-field
              :model-value="rule.surcharge_minutes"
              type="number"
              min="0"
              density="compact"
              variant="plain"
              hide-details
              suffix="min"
              @update:model-value="
                (value) => updateRule(rule, 'surcharge_minutes', value)
              "
            ></v-text-field>
          </td>
          <td>
            <v-btn
              icon="mdi-delete"
              size="x-small"
              variant="text"
              :loading="saving"
              @click="removeRule(rule)"
            ></v-btn>
          </td>
        </tr>
      </tbody>
    </v-table>

    <p v-if="dirty" class="text-caption text-warning mt-2">
      Geänderte Werte sind noch nicht gespeichert.
    </p>

    <div v-if="dirty" class="d-flex ga-2 mt-3">
      <v-btn color="primary" variant="flat" :loading="saving" @click="saveAll">
        Änderungen speichern
      </v-btn>
      <v-btn variant="text" :disabled="saving" @click="reload">
        Verwerfen
      </v-btn>
    </div>
  </v-container>
</template>

<script lang="ts">
import { defineComponent } from "vue";

import { transportLabel } from "../config/transportLabels";
import {
  usePlzSurchargeStore,
  type PlzSurcharge,
} from "../store/plzSurchargeStore";
import { useOfficeStore } from "../store/officeStore";

export default defineComponent({
  name: "ThePlzSurcharges",

  props: {
    officeId: {
      type: Number,
      required: true,
      validator: (value: number) => value > 0,
    },
  },

  data() {
    return {
      rules: [] as PlzSurcharge[],
      newPlz: "",
      newTransport: "car",
      newPct: 0 as number,
      newMinutes: 0 as number,
      saving: false,
      dirty: false,
      error: "",
    };
  },

  computed: {
    store() {
      return usePlzSurchargeStore();
    },
    officeName(): string {
      return useOfficeStore().selectedOffice?.office_name ?? "diesen Standort";
    },
    transportItems() {
      return this.store.transports.map((transport) => ({
        value: transport.transport_type,
        label:
          transportLabel(transport.transport_type) +
          (transport.road_bound ? " (Straße)" : ""),
      }));
    },
    plzCount(): number {
      return new Set(this.rules.map((rule) => rule.plz)).size;
    },
    sortedRules(): PlzSurcharge[] {
      return [...this.rules].sort(
        (a, b) =>
          a.plz.localeCompare(b.plz) ||
          a.transport_type.localeCompare(b.transport_type),
      );
    },
    newPlzError(): string[] {
      if (!this.newPlz) return [];
      if (!/^\d{4,5}$/.test(this.newPlz)) {
        return ["Bitte eine 4- oder 5-stellige PLZ eingeben."];
      }
      return [];
    },
    canAdd(): boolean {
      return (
        this.newPlz.length >= 4 &&
        this.newPlzError.length === 0 &&
        Boolean(this.newTransport) &&
        (this.newPct > 0 || this.newMinutes > 0)
      );
    },
  },

  watch: {
    officeId: {
      immediate: true,
      handler() {
        this.reload();
      },
    },
  },

  async mounted() {
    // Die Liste der Verkehrsmittel kommt vom Backend, damit Oberfläche und
    // Validierung nicht auseinanderlaufen.
    await this.store.fetchTransports().catch(() => undefined);
  },

  methods: {
    labelFor(transportType: string): string {
      return transportLabel(transportType);
    },
    ruleKey(rule: PlzSurcharge): string {
      return `${rule.plz}|${rule.transport_type}`;
    },
    async reload() {
      if (!this.officeId) return;
      this.error = "";
      try {
        await this.store.fetchSurcharges(this.officeId);
        // Eigene Kopie: die Tabelle bearbeitet Werte, bevor gespeichert wird.
        this.rules = this.store.surcharges.map((rule) => ({ ...rule }));
        this.dirty = false;
      } catch {
        this.error = this.store.error ?? "Laden fehlgeschlagen";
      }
    },
    updateRule(rule: PlzSurcharge, field: keyof PlzSurcharge, value: unknown) {
      const numeric = Number(value);
      (rule[field] as unknown as number) = Number.isFinite(numeric)
        ? numeric
        : 0;
      this.dirty = true;
    },
    async addRule() {
      if (!this.canAdd) return;
      await this.persist([
        ...this.rules,
        {
          plz: this.newPlz,
          transport_type: this.newTransport,
          surcharge_pct: Number(this.newPct) || 0,
          surcharge_minutes: Number(this.newMinutes) || 0,
        } as PlzSurcharge,
      ]);
      this.newPlz = "";
      this.newPct = 0;
      this.newMinutes = 0;
    },
    async removeRule(rule: PlzSurcharge) {
      // Aufschlag 0 in beiden Feldern entfernt die Regel - so macht es das
      // Backend, und der Weg ist derselbe wie beim Speichern.
      await this.persist(
        this.rules.map((entry) =>
          this.ruleKey(entry) === this.ruleKey(rule)
            ? { ...entry, surcharge_pct: 0, surcharge_minutes: 0 }
            : entry,
        ),
      );
    },
    async saveAll() {
      await this.persist(this.rules);
    },
    async persist(entries: PlzSurcharge[]) {
      this.saving = true;
      this.error = "";
      try {
        await this.store.saveSurcharges(this.officeId, entries);
        this.rules = this.store.surcharges.map((rule) => ({ ...rule }));
        this.dirty = false;
        this.$emit("surcharges-saved");
      } catch {
        this.error = this.store.error ?? "Speichern fehlgeschlagen";
      } finally {
        this.saving = false;
      }
    },
  },

  emits: ["surcharges-saved"],
});
</script>

<style scoped>
/* Gleiche Behandlung wie in TheParameters.vue: Der Import-Bildschirm liegt
   ueber einem Hintergrundbild, ohne eigene Flaeche waere der Abschnitt kaum
   lesbar. Die Werte sind bewusst identisch zu dort. */
.plz-surcharge-container {
  backdrop-filter: blur(4px);
  background-color: rgba(255, 255, 255, 0.9) !important;
  border-radius: 8px;
}

table {
  backdrop-filter: blur(4px);
  background-color: rgba(255, 255, 255, 0.95) !important;
  border-radius: 8px;
  overflow: hidden;
}

table th {
  background-color: rgba(255, 255, 255, 0.9) !important;
}

table tr:nth-child(even) {
  background-color: rgba(240, 240, 240, 0.9) !important;
}

table tr:nth-child(odd) {
  background-color: rgba(255, 255, 255, 0.8) !important;
}
</style>
